import type { LGraphNode } from '@comfyorg/litegraph'
import { IComboWidget } from '@comfyorg/litegraph/dist/types/widgets'

import { useNodeImage, useNodeVideo } from '@/composables/node/useNodeImage'
import { useNodeImageUpload } from '@/composables/node/useNodeImageUpload'
import { useValueTransform } from '@/composables/useValueTransform'
import { t } from '@/i18n'
import type { ResultItem } from '@/schemas/apiSchema'
import type { InputSpec } from '@/schemas/nodeDefSchema'
import type { ComfyWidgetConstructor } from '@/scripts/widgets'
import { useNodeOutputStore } from '@/stores/imagePreviewStore'
import { createAnnotatedPath } from '@/utils/formatUtil'
import { addToComboValues } from '@/utils/litegraphUtil'

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp'
const ACCEPTED_VIDEO_TYPES = 'video/webm,video/mp4'

type InternalFile = string | ResultItem
type InternalValue = InternalFile | InternalFile[]
type ExposedValue = string | string[]

const isImageFile = (file: File) => file.type.startsWith('image/')
const isVideoFile = (file: File) => file.type.startsWith('video/')

const findFileComboWidget = (node: LGraphNode, inputName: string) =>
  node.widgets!.find((w) => w.name === inputName) as IComboWidget & {
    value: ExposedValue
  }

export const useImageUploadWidget = () => {
  const widgetConstructor: ComfyWidgetConstructor = (
    node: LGraphNode,
    inputName: string,
    inputData: InputSpec
  ) => {
    const inputOptions = inputData[1] ?? {}
    const { imageInputName, allow_batch, image_folder = 'input' } = inputOptions
    const nodeOutputStore = useNodeOutputStore()

    function showImage(name: string) {
      const img = new Image()
      img.onload = () => {

        (node as any).imgs = [img]
        app.graph.setDirtyCanvas(true)
      }
      img.src = name
      
      node.setSizeForImage?.()
    }

    const isAnimated = !!inputOptions.animated_image_upload
    const isVideo = !!inputOptions.video_upload
    // const accept = isVideo ? ACCEPTED_VIDEO_TYPES : ACCEPTED_IMAGE_TYPES

    const { showPreview } = isVideo ? useNodeVideo(node) : useNodeImage(node)

    const fileFilter = isVideo ? isVideoFile : isImageFile
    // @ts-expect-error InputSpec is not typed correctly
    const fileComboWidget :any = findFileComboWidget(node, imageInputName)
    const initialFile = `${fileComboWidget.value}`
    const formatPath = (value: InternalFile) =>
      // @ts-expect-error InputSpec is not typed correctly
      createAnnotatedPath(value, { rootFolder: image_folder })

    const transform = (internalValue: InternalValue): ExposedValue => {
      if (!internalValue) return initialFile
      if (Array.isArray(internalValue))
        return allow_batch
          ? internalValue.map(formatPath)
          : formatPath(internalValue[0])
      return formatPath(internalValue)
    }

    Object.defineProperty(
      fileComboWidget,
      'value',
      useValueTransform(transform, initialFile)
    )

    // Setup file upload handling
    // const { openFileSelection } = useNodeImageUpload(node, {
    //   // @ts-expect-error InputSpec is not typed correctly
    //   allow_batch,
    //   fileFilter,
    //   accept,
    //   onUploadComplete: (output) => {
    //     output.forEach((path) => addToComboValues(fileComboWidget, path))
    //     // @ts-expect-error litegraph combo value type does not support arrays yet
    //     fileComboWidget.value = output
    //     fileComboWidget.callback?.(output)
    //   }
    // })

    const fileInput: any = document.createElement('input')
    Object.assign(fileInput, {
      type: 'text',
      accept: 'image/jpeg,image/png,image/webp',
      style: 'display: none',
      // onchange: async () => {
      //   if (fileInput.files.length) {
      //     await uploadFile(fileInput.files[0], true)
      //   }
      // },
      onclick: async () => {
        //检查一下是否登录，没登录不能上传图片
				let userInfo = (top as any).localStorage.getItem('userInfo');
				userInfo = userInfo && JSON.parse(userInfo);
				if (!userInfo || !userInfo?.id) {
					(top as any)?.$EventBus?.$emit("openLoginDialog");
					return;
				}

        // 打开外层弹窗，传递节点ID
        (top as any).postMessage({ 
          type: 'openImageUploadModal', 
          info: { 
            nodeId: node.id 
          } 
        }, '*')
      }
    })
    document.body.append(fileInput)

    // Create the button widget for selecting the files
    const uploadWidget = node.addWidget(
      'button',
      inputName,
      'image',
      // () => openFileSelection(),
      () =>  fileInput.click(),
      {
        serialize: false
      }
    )
    uploadWidget.label = t('g.choose_file_to_upload')

    // 设置上传图片信息
    function setUploadImageInfo(url: string, width: number, height: number) {
      if (!fileComboWidget.options.values.includes(url)) {
        fileComboWidget.options.values.push(url);
        // todo 这里是和后端约定的值，将图片真实宽高传给了后端
        uploadWidget.value = `${width}@${height}`
      }

        fileComboWidget.value = url;
        fileComboWidget.callback?.(url)
        addToComboValues(fileComboWidget, url)
    }

    // 将 setUploadImageInfo 方法注册到节点上，以便外部调用
    ;(node as any).setUploadImageInfo = setUploadImageInfo

    // 自定义上传
    async function uploadFile(file: File, updateNode: boolean, pasted = false) {
			try {
				//检查一下是否登录，没登录不能上传图片
				let userInfo = (top as any).localStorage.getItem('userInfo');
				userInfo = userInfo && JSON.parse(userInfo);
				if (!userInfo || !userInfo?.id) {
					(top as any)?.$EventBus?.$emit("openLoginDialog");
					return;
				}
				let {width, height} = await checkImageSize(file);
				let UPLOAD = (top as any)?.workflow_upload;
				UPLOAD
					.startUpload(file, {category: 20, sub_category: 1})
					.then((url: any) => {

            // 设置上传图片信息
						setUploadImageInfo(url, width, height)

					})
					.catch((err: any) => {
            console.error(err);
					});
			} catch (error) {
        console.error(error);
			}
		}

    // 读取图片宽高
		async function checkImageSize(file: File): Promise<any> {
			return new Promise((resolve,reject)=>{
				let reader = new FileReader();
				reader.onload = function(event) {
					let img:any = new Image();
					img.onload = function() {
						let width = img.naturalWidth;
						let height = img.naturalHeight;
						resolve({width, height});
					};
					img.onerror = function (){
						reject(false);
					}
					img.src = event.target.result;
				};
				reader.readAsDataURL(file);
			});
		}

    // @ts-expect-error
    node.onDragOver = function (e) {
      if (e.dataTransfer && e.dataTransfer.items) {
        const image = [...e.dataTransfer.items].find((f) => f.kind === 'file')
        return !!image
      }

      return false
    }

    // On drop upload files
    // @ts-expect-error
    node.onDragDrop = function (e) {
      console.log('onDragDrop called')
      let handled = false
      for (const file of e.dataTransfer.files) {
        if (file.type.startsWith('image/')) {
          uploadFile(file, !handled) // Dont await these, any order is fine, only update on first one
          handled = true
        }
      }

      return handled
    }

    // @ts-expect-error
    node.pasteFile = function (file) {
      if (file.type.startsWith('image/')) {
        const is_pasted =
          file.name === 'image.png' && file.lastModified - Date.now() < 2000
        uploadFile(file, true, is_pasted)
        return true
      }
      return false
    }

    // Add our own callback to the combo widget to render an image when it changes
    const cb = node.callback
    fileComboWidget.callback = function () {
      showImage(fileComboWidget.value)
      if (cb) {
        return cb.apply(this, arguments)
      }
      // nodeOutputStore.setNodeOutputs(node, fileComboWidget.value, {
      //   isAnimated
      // })
      // node.graph?.setDirtyCanvas(true)
    }

    // On load if we have a value then render the image
    // The value isnt set immediately so we need to wait a moment
    // No change callbacks seem to be fired on initial setting of the value
    requestAnimationFrame(() => {
      // nodeOutputStore.setNodeOutputs(node, fileComboWidget.value, {
      //   isAnimated
      // })
      // // 转换格式
      // showPreview({ block: false })

      if (fileComboWidget.value) {
          showImage(fileComboWidget.value)
        }
    })

    return { widget: uploadWidget }
  }

  return widgetConstructor
}
